/**
 * analytics/ChatInterface.jsx
 * Phase 2 Step 12 — Main chat interface for conversational analytics.
 *
 * Handles:
 * - Free-form question input + send
 * - Conversation history (questions + rendered responses)
 * - Partial match confirmation UI
 * - Clarification input UI
 * - Guided questions sidebar (click → auto-submit)
 */

import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';

import { useAuth } from '../../App';
import OutputRenderer from './OutputRenderer';
import GuidedQuestions from './GuidedQuestions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/analytics`;

/* ── Main Component ──────────────────────────────────────────────────────── */
const ChatInterface = ({ session, onSessionEnd }) => {
  const { user } = useAuth();
  const userId = user?.id || '';

  const [messages, setMessages]               = useState([]);
  const [inputText, setInputText]             = useState('');
  const [isLoading, setIsLoading]             = useState(false);
  const [pendingAction, setPendingAction]     = useState(null); // {type, data}
  const [clarificationText, setClarificationText] = useState('');

  const bottomRef = useRef(null);

  // Scroll to bottom on new messages
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  /* ── Helpers ─────────────────────────────────────────────────────────── */
  const sessionId     = session?.session_id;
  const guidedQs      = session?.guided_questions || [];
  const datasetIds    = session?.dataset_ids || [];
  const mrsId         = session?.mrs_id || '';

  const addMessage = (role, content) => {
    setMessages((prev) => [
      ...prev,
      { role, content, timestamp: new Date().toISOString() },
    ]);
  };

  /* ── Send a question ─────────────────────────────────────────────────── */
  const sendQuestion = async (question) => {
    if (!question.trim() || isLoading) return;

    addMessage('user', question);
    setInputText('');
    setIsLoading(true);
    setPendingAction(null);

    try {
      const res = await axios.post(`${API}/query/chat?user_id=${userId}`, {
        session_id: sessionId,
        question,
      });
      handleResponse(res.data, question);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Something went wrong.';
      addMessage('error', typeof detail === 'string' ? detail : JSON.stringify(detail));
    } finally {
      setIsLoading(false);
    }
  };

  /* ── Handle backend response ─────────────────────────────────────────── */
  const handleResponse = (data, originalQuestion) => {
    if (data.action === 'confirm') {
      // Partial match — ask user to confirm
      setPendingAction({
        type: 'confirm',
        data: {
          suggestedQuestion:   data.suggested_question,
          suggestedQuestionId: data.suggested_question_id,
          originalQuestion,
        },
      });
      addMessage('assistant_confirm', data);
    } else if (data.action === 'clarify') {
      // Agent needs more info
      setPendingAction({ type: 'clarify', data });
      addMessage('assistant_clarify', data);
    } else if (data.action === 'validation_failed' || data.action === 'execution_failed') {
      const details = Array.isArray(data.details) && data.details.length
        ? '\n' + data.details.join('\n')
        : '';
      addMessage('error', (data.error || 'Query failed.') + details);
    } else {
      // Normal analytics response
      addMessage('assistant', data);
    }
  };

  /* ── Partial match: user confirms or rejects ─────────────────────────── */
  const handleConfirm = async (confirmed) => {
    if (!pendingAction || pendingAction.type !== 'confirm') return;
    const { suggestedQuestionId, originalQuestion } = pendingAction.data;

    setIsLoading(true);
    setPendingAction(null);
    try {
      const res = await axios.post(`${API}/query/confirm?user_id=${userId}`, {
        session_id:            sessionId,
        confirmed,
        suggested_question_id: suggestedQuestionId,
        original_question:     originalQuestion,
      });
      handleResponse(res.data, originalQuestion);
    } catch (err) {
      addMessage('error', 'Failed to confirm suggestion.');
    } finally {
      setIsLoading(false);
    }
  };

  /* ── Clarification: user submits answer ──────────────────────────────── */
  const handleClarification = async () => {
    if (!clarificationText.trim() || !pendingAction || pendingAction.type !== 'clarify') return;
    const originalQuestion = pendingAction.data?.original_question || '';

    setIsLoading(true);
    setPendingAction(null);
    const answer = clarificationText;
    setClarificationText('');
    try {
      const res = await axios.post(`${API}/query/clarify?user_id=${userId}`, {
        session_id:          sessionId,
        original_question:   originalQuestion,
        clarification_answer: answer,
      });
      handleResponse(res.data, `${originalQuestion} — ${answer}`);
    } catch (err) {
      addMessage('error', 'Failed to submit clarification.');
    } finally {
      setIsLoading(false);
    }
  };

  /* ── Dataset disambiguation: user picked a specific group ─────────────── */
  const handleSelectGroup = async (actionData) => {
    const originalQuestion = actionData.original_question || '';
    const explicitGroupId  = actionData.selected_group_id || null;
    if (!originalQuestion || !explicitGroupId) return;

    // Remove the disambiguation card from messages
    setMessages((prev) => {
      const withoutCard = [...prev];
      for (let i = withoutCard.length - 1; i >= 0; i--) {
        if (withoutCard[i].role === 'assistant' &&
            withoutCard[i].content?.action === 'dataset_disambiguation') {
          withoutCard.splice(i, 1);
          break;
        }
      }
      return withoutCard;
    });

    addMessage('user', originalQuestion);
    setIsLoading(true);
    try {
      const res = await axios.post(`${API}/query/chat?user_id=${userId}`, {
        session_id:         sessionId,
        question:           originalQuestion,
        explicit_group_id:  explicitGroupId,
      });
      handleResponse(res.data, originalQuestion);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Something went wrong.';
      addMessage('error', typeof detail === 'string' ? detail : JSON.stringify(detail));
    } finally {
      setIsLoading(false);
    }
  };

  /* ── Guided question result from sidebar ─────────────────────────────── */
  // Called by GuidedQuestions when the guided endpoint returns successfully.
  // Adds the question as a user bubble first, then the response.
  const handleGuidedResult = (response, questionText) => {
    if (isLoading) return; // prevent overlap with an in-flight chat request
    addMessage('user', questionText);
    setIsLoading(true);
    // Defer result so the loading spinner has a chance to render
    setTimeout(() => {
      addMessage('assistant', response);
      setIsLoading(false);
    }, 0);
  };

  /* ── Render ──────────────────────────────────────────────────────────── */
  return (
    <div className="flex h-full gap-4">

      {/* ── Guided Questions Sidebar ─────────────────────────────────── */}
      <aside className="w-72 shrink-0 flex flex-col min-h-0 border-r border-gray-200 pr-4">
        <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-3 shrink-0">
          Suggested Questions
        </h3>
        <div className="overflow-y-auto flex-1 min-h-0">
        <GuidedQuestions
          questions={guidedQs}
          datasetId={datasetIds[0]}
          mrsId={mrsId}
          userId={userId}
          onResult={handleGuidedResult}
        />
        </div>
      </aside>

      {/* ── Main Chat Area ────────────────────────────────────────────── */}
      <div className="flex flex-col flex-1 min-w-0 min-h-0">

        {/* Conversation history — fixed height, scrolls internally */}
        <div className="flex-1 min-h-0 overflow-y-auto space-y-4 pb-4 pr-1">
          {messages.length === 0 && (
            <div className="text-center py-16 text-gray-400">
              <p className="text-lg font-medium">Ask a question about your dataset</p>
              <p className="text-sm mt-1">
                Try selecting a suggested question from the sidebar, or type your own below.
              </p>
            </div>
          )}

          {messages.map((msg, i) => (
            <MessageBubble
              key={i}
              message={msg}
              onConfirm={(action, data) => {
                if (action === 'select_group') handleSelectGroup(data);
              }}
            />
          ))}

          {/* Partial match confirmation banner */}
          {pendingAction?.type === 'confirm' && (
            <ConfirmBanner
              suggestion={pendingAction.data.suggestedQuestion}
              onYes={() => handleConfirm(true)}
              onNo={() => handleConfirm(false)}
            />
          )}

          {/* Clarification input */}
          {pendingAction?.type === 'clarify' && (
            <ClarificationPanel
              question={pendingAction.data?.clarification_question || ''}
              value={clarificationText}
              onChange={setClarificationText}
              onSubmit={handleClarification}
              disabled={isLoading}
            />
          )}

          <div ref={bottomRef} />
        </div>

        {/* Input area */}
        <div className="border-t border-gray-200 pt-4">
          <div className="flex gap-2">
            <input
              type="text"
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && sendQuestion(inputText)}
              placeholder="Ask a question about your data..."
              disabled={isLoading}
              className="flex-1 rounded-lg border border-gray-300 px-4 py-2 text-sm
                         focus:outline-none focus:ring-2 focus:ring-blue-500
                         disabled:opacity-50 disabled:bg-gray-50"
            />
            <button
              onClick={() => sendQuestion(inputText)}
              disabled={isLoading || !inputText.trim()}
              className="px-5 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium
                         hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed
                         transition-colors"
            >
              {isLoading ? '...' : 'Send'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ── Message Bubble ──────────────────────────────────────────────────────── */
const MessageBubble = ({ message, onConfirm }) => {
  const { role, content, timestamp } = message;

  if (role === 'user') {
    return (
      <div className="flex justify-end">
        <div className="max-w-lg rounded-2xl rounded-tr-sm bg-blue-600 px-4 py-3 text-white text-sm shadow-sm">
          <p>{content}</p>
          <p className="text-xs text-blue-200 mt-1 text-right">
            {new Date(timestamp).toLocaleTimeString()}
          </p>
        </div>
      </div>
    );
  }

  if (role === 'assistant') {
    return (
      <div className="flex justify-start">
        <div className="max-w-2xl w-full rounded-2xl rounded-tl-sm bg-white border border-gray-200 p-4 shadow-sm">
          <OutputRenderer response={content} onConfirm={onConfirm} />
          <p className="text-xs text-gray-400 mt-2">
            {new Date(timestamp).toLocaleTimeString()}
          </p>
        </div>
      </div>
    );
  }

  if (role === 'error') {
    return (
      <div className="flex justify-start">
        <div className="max-w-lg rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-red-700 text-sm">
          {content}
        </div>
      </div>
    );
  }

  // assistant_confirm / assistant_clarify — rendered as pending action; no bubble needed
  return null;
};

/* ── Confirm Banner ──────────────────────────────────────────────────────── */
const ConfirmBanner = ({ suggestion, onYes, onNo }) => (
  <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 flex items-center justify-between gap-4">
    <p className="text-sm text-blue-800">
      <span className="font-semibold">Did you mean: </span>
      "{suggestion}"
    </p>
    <div className="flex gap-2 shrink-0">
      <button
        onClick={onYes}
        className="px-3 py-1.5 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700"
      >
        Yes
      </button>
      <button
        onClick={onNo}
        className="px-3 py-1.5 rounded-lg border border-gray-300 text-gray-700 text-sm hover:bg-gray-50"
      >
        No, ask differently
      </button>
    </div>
  </div>
);

/* ── Clarification Panel ─────────────────────────────────────────────────── */
const ClarificationPanel = ({ question, value, onChange, onSubmit, disabled }) => (
  <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-4 space-y-3">
    <p className="text-sm text-amber-800 font-medium">{question}</p>
    <div className="flex gap-2">
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && onSubmit()}
        placeholder="Your answer..."
        disabled={disabled}
        className="flex-1 rounded-lg border border-amber-300 bg-white px-3 py-2 text-sm
                   focus:outline-none focus:ring-2 focus:ring-amber-500 disabled:opacity-50"
      />
      <button
        onClick={onSubmit}
        disabled={disabled || !value.trim()}
        className="px-4 py-2 rounded-lg bg-amber-600 text-white text-sm font-medium
                   hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        Submit
      </button>
    </div>
  </div>
);

/* ── PropTypes ───────────────────────────────────────────────────────────── */
ChatInterface.propTypes = {
  session:      PropTypes.object.isRequired,
  onSessionEnd: PropTypes.func,
};

ChatInterface.defaultProps = {
  onSessionEnd: () => {},
};

MessageBubble.propTypes = {
  message: PropTypes.shape({
    role:      PropTypes.string.isRequired,
    content:   PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    timestamp: PropTypes.string,
  }).isRequired,
  onConfirm: PropTypes.func,
};

ConfirmBanner.propTypes = {
  suggestion: PropTypes.string.isRequired,
  onYes:      PropTypes.func.isRequired,
  onNo:       PropTypes.func.isRequired,
};

ClarificationPanel.propTypes = {
  question: PropTypes.string.isRequired,
  value:    PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

export default ChatInterface;
